import React, { useState } from "react";
import { Camera, Globe2, ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { CameraAngle, ACTIVITIES, ActivityId } from "../mockData";

/* ---------- Collapsible filter card ---------- */

export type FilterChipSummary = {
  label: string;        // e.g. "Sport"
  value: string;        // e.g. "Golf"
  emoji?: string;
  onClear?: () => void; // optional — shows an x to reset
};

export function FilterCard({
  search,
  summary,
  countActive = 0,
  defaultOpen = false,
  rightHint,
  children,
}: {
  search?: React.ReactNode;
  summary?: FilterChipSummary[];
  countActive?: number;
  defaultOpen?: boolean;
  rightHint?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="card p-3">
      <div className="flex flex-wrap items-center gap-3">
        {search && <div className="flex-1 min-w-[220px]">{search}</div>}

        {/* summary chips when collapsed */}
        {summary && summary.length > 0 && !open && (
          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
            {summary.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 h-7 px-2 pr-1 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700"
              >
                <span className="text-slate-400">{s.label}:</span>
                {s.emoji && <span className="text-sm leading-none">{s.emoji}</span>}
                <span className="font-medium">{s.value}</span>
                {s.onClear && (
                  <button
                    onClick={s.onClear}
                    className="ml-0.5 h-4 w-4 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 inline-flex items-center justify-center"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {rightHint}
          <button
            onClick={() => setOpen((v) => !v)}
            className={[
              "h-9 inline-flex items-center gap-1.5 px-2.5 rounded-md border text-sm font-medium transition",
              open
                ? "bg-el-50 border-el-200 text-el-800"
                : "bg-white border-slate-200 text-slate-600 hover:text-slate-800",
            ].join(" ")}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Filters</span>
            {countActive > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-el-500 text-white text-[0.65rem] font-semibold">
                {countActive}
              </span>
            )}
            <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 border-t border-slate-100 pt-4 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

/* ---------- Filter row + chip primitives ---------- */

export function FilterRow({
  label, children, hint,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span className="label-eyebrow">{label}</span>
        {hint && <span className="text-[0.7rem] text-slate-400">{hint}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

type ChipProps = {
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  emoji?: string;
  label: string;
  hint?: string;
};

export function FilterChip({ active, onClick, icon, emoji, label, hint }: ChipProps) {
  return (
    <button
      onClick={onClick}
      className={[
        "h-9 inline-flex items-center gap-2 px-2.5 pr-3 rounded-md border transition text-sm",
        active
          ? "bg-el-50 border-el-300 ring-1 ring-el-200 text-slate-900"
          : "bg-white border-slate-200 text-slate-600 hover:border-el-200 hover:text-slate-800",
      ].join(" ")}
    >
      {emoji ? (
        <span className="text-base leading-none">{emoji}</span>
      ) : icon ? (
        <span className={active ? "text-el-600" : "text-slate-400"}>{icon}</span>
      ) : null}
      <span className="font-medium leading-tight">
        {label}
        {hint && <span className="ml-1 text-[0.7rem] text-slate-400 font-normal">{hint}</span>}
      </span>
    </button>
  );
}

/* ---------- Outcome-score range (dual slider) ---------- */

// Same visual language as Explore's local ScoreRange (0–100, step 5, gradient fill between
// the thumbs), lifted here so the sessions list can use it as a shared primitive. Labelled
// for accessibility/tests: "Minimum score" / "Maximum score".
export function ScoreRange({
  min, max, onChange,
}: {
  min: number; max: number;
  onChange: (lo: number, hi: number) => void;
}) {
  const FULL_MIN = 0, FULL_MAX = 100;
  const loPct = ((min - FULL_MIN) / (FULL_MAX - FULL_MIN)) * 100;
  const hiPct = ((max - FULL_MIN) / (FULL_MAX - FULL_MIN)) * 100;

  return (
    <div className="max-w-xs">
      <div className="flex items-baseline gap-2 mb-2">
        <span className="label-eyebrow">Score range</span>
        <span className="text-[0.7rem] text-slate-500 tabular-nums">{min} – {max}</span>
      </div>
      <div className="relative h-9">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-slate-100" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-gradient-to-r from-el-400 to-el-600"
          style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}
        />
        <input
          type="range" min={FULL_MIN} max={FULL_MAX} step={5}
          aria-label="Minimum score"
          value={min}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(Math.min(v, max - 5), max);
          }}
          className="range-thumb absolute inset-0 w-full bg-transparent appearance-none pointer-events-none"
          style={{ zIndex: 3 }}
        />
        <input
          type="range" min={FULL_MIN} max={FULL_MAX} step={5}
          aria-label="Maximum score"
          value={max}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(min, Math.max(v, min + 5));
          }}
          className="range-thumb absolute inset-0 w-full bg-transparent appearance-none pointer-events-none"
          style={{ zIndex: 4 }}
        />
      </div>
      <div className="flex justify-between text-[0.65rem] text-slate-400 tabular-nums mt-0.5">
        <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
      </div>
    </div>
  );
}

/* ---------- Sport row ---------- */

const ACTIVITY_FAMILIES: { key: string; emoji: string; label: string }[] = [
  { key: "Golf",      emoji: "🏌️", label: "Golf" },
  { key: "Tennis",    emoji: "🎾", label: "Tennis" },
  { key: "Cricket",   emoji: "🏏", label: "Cricket" },
  { key: "Bowling",   emoji: "🎳", label: "Bowling" },
  { key: "Movement",  emoji: "🏃", label: "Movement" },
  { key: "Dance",     emoji: "💃", label: "Dance" },
  { key: "Workplace", emoji: "🏭", label: "Workplace" },
];

export function ActivityFilter({
  value, onChange, granularity = "family",
}: {
  value: string;
  onChange: (v: string) => void;
  granularity?: "family" | "activityId";
}) {
  if (granularity === "family") {
    return (
      <FilterRow label="Activity">
        <FilterChip active={value === "all"} onClick={() => onChange("all")} icon={<Globe2 className="h-4 w-4" />} label="All" />
        {ACTIVITY_FAMILIES.map((s) => (
          <FilterChip
            key={s.key}
            active={value === s.key}
            // Clicking the already-selected sport toggles back to "All" (the
            // default) so the filter can be cleared without hunting for "All".
            onClick={() => onChange(value === s.key ? "all" : s.key)}
            emoji={s.emoji}
            label={s.label}
          />
        ))}
      </FilterRow>
    );
  }

  // activityId-level: full template list (golf face-on, DTL, etc.)
  return (
    <FilterRow label="Activity template">
      <FilterChip active={value === "all"} onClick={() => onChange("all")} icon={<Globe2 className="h-4 w-4" />} label="All" />
      {ACTIVITIES.map((s) => (
        <FilterChip
          key={s.id}
          active={value === s.id}
          onClick={() => onChange(value === s.id ? "all" : s.id)}
          emoji={s.emoji}
          label={s.label.replace(/^[^—]+ — /, "")}
          hint={s.label.split(" — ")[0]}
        />
      ))}
    </FilterRow>
  );
}

/**
 * Multi-select variant of the family-level ActivityFilter (Sessions wants several sports
 * at once; the single-select version stays for the pages built around one). "All" = empty
 * selection; clicking a family toggles it in/out of the set.
 */
export function ActivityMultiFilter({
  values, onChange,
}: {
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (key: string) =>
    onChange(values.includes(key) ? values.filter((v) => v !== key) : [...values, key]);
  return (
    <FilterRow label="Activity">
      <FilterChip active={values.length === 0} onClick={() => onChange([])} icon={<Globe2 className="h-4 w-4" />} label="All" />
      {ACTIVITY_FAMILIES.map((s) => (
        <FilterChip
          key={s.key}
          active={values.includes(s.key)}
          onClick={() => toggle(s.key)}
          emoji={s.emoji}
          label={s.label}
        />
      ))}
    </FilterRow>
  );
}

/* ---------- Camera angle row ---------- */

// Tiny SVG glyph showing camera position relative to the athlete.
function AngleGlyph({ angle, active }: { angle: CameraAngle; active: boolean }) {
  const stroke = active ? "#047857" : "#94a3b8";
  const fill   = active ? "#10b981" : "#cbd5e1";
  // 24x16 viewBox; person on the right, camera dot on the left/top/bottom indicating angle
  const cam = {
    "Face-on": { cx: 4,  cy: 8 },
    "DTL":     { cx: 6,  cy: 4 },
    "Side":    { cx: 12, cy: 2 },
    "Rear":    { cx: 20, cy: 8 },
  }[angle];
  return (
    <svg viewBox="0 0 24 16" className="h-4 w-6">
      {/* athlete dot */}
      <circle cx={14} cy={8} r={1.6} fill={fill} />
      <line x1={14} y1={9.6} x2={14} y2={13} stroke={fill} strokeWidth={1} />
      {/* camera */}
      <rect x={cam.cx - 1.2} y={cam.cy - 1.2} width={2.4} height={2.4} fill={stroke} rx={0.4} />
      {/* sight line */}
      <line x1={cam.cx} y1={cam.cy} x2={14} y2={8} stroke={stroke} strokeWidth={0.8} strokeDasharray="1.5 1.5" />
    </svg>
  );
}

const ANGLES: CameraAngle[] = ["Face-on", "DTL", "Side", "Rear"];

export function AngleFilter({
  value, onChange,
}: { value: string; onChange: (v: string) => void }) {
  return (
    <FilterRow label="Camera angle">
      <FilterChip
        active={value === "all"}
        onClick={() => onChange("all")}
        icon={<Camera className="h-4 w-4" />}
        label="All"
      />
      {ANGLES.map((a) => (
        <FilterChip
          key={a}
          active={value === a}
          onClick={() => onChange(value === a ? "all" : a)}
          icon={<AngleGlyph angle={a} active={value === a} />}
          label={a}
        />
      ))}
    </FilterRow>
  );
}

/* ---------- Metric row ---------- */

export function MetricFilter<T extends string>({
  options, value, onChange, label = "Metric",
}: {
  label?: string;
  options: { key: T; label: string; unit?: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <FilterRow label={label}>
      {options.map((o) => (
        <FilterChip
          key={o.key}
          active={value === o.key}
          onClick={() => onChange(o.key)}
          label={o.label}
          hint={o.unit}
        />
      ))}
    </FilterRow>
  );
}

/* ---------- Generic option row ---------- */

export function OptionFilter<T extends string>({
  label, options, value, onChange,
}: {
  label: string;
  options: { value: T; label: string; emoji?: string }[];
  value: T | "all";
  onChange: (v: T | "all") => void;
}) {
  return (
    <FilterRow label={label}>
      <FilterChip active={value === "all"} onClick={() => onChange("all")} icon={<Globe2 className="h-4 w-4" />} label="All" />
      {options.map((o) => (
        <FilterChip
          key={o.value}
          active={value === o.value}
          onClick={() => onChange(value === o.value ? "all" : o.value)}
          emoji={o.emoji}
          label={o.label}
        />
      ))}
    </FilterRow>
  );
}

// Helper used by Progress / Library to map a ActivityId or family to a SportFamily key.
export function activityToFamily(activityId: ActivityId): string {
  return ACTIVITIES.find((s) => s.id === activityId)?.group ?? "Movement";
}

export const ACTIVITY_FAMILY_OPTIONS = ACTIVITY_FAMILIES;
